import type { MouseEvent } from 'react';
import { Tooltip } from './Tooltip';
import { CASE_LINKS_BRAND, DM_LOGO, type CaseLinksManufacturer } from './caseLinkLogos';

export interface CaseLinksProps {
    manufacturer: CaseLinksManufacturer;
    manufacturerLink?: string | null;
    dmLink?: string | null;
    manufacturerOnly?: boolean;
    /** Extra classes for the row, e.g. its justification. */
    className?: string;
}

const LIVE_CHIP = 'cursor-pointer border-gray-200 bg-white hover:bg-gray-100 hover:border-gray-300';
// Still hoverable so its tooltip explains why; guardClick stops the click.
const MISSING_CHIP = 'grayscale opacity-30 cursor-not-allowed border-transparent';

// Rows are often clickable (e.g. the patient table), so a chip click never bubbles up.
function guardClick(href: string | null | undefined) {
    return (e: MouseEvent<HTMLAnchorElement>) => {
        e.stopPropagation();
        if (!href) e.preventDefault();
    };
}

// The manufacturer wordmark and the DM icon as link chips. The manufacturer chip is a fixed 72px box
// so Spark and Invisalign logos, with different aspect ratios, don't shift the DM chip.
export function CaseLinks({
    manufacturer,
    manufacturerLink,
    dmLink,
    manufacturerOnly = false,
    className
}: CaseLinksProps) {
    const brand = CASE_LINKS_BRAND[manufacturer];
    return (
        <div className={`flex items-center gap-3 ${className ?? ''}`}>
            <Tooltip
                content={
                    manufacturerLink ? 'Open treatment plan' : 'No treatment plan link available'
                }
            >
                <a
                    href={manufacturerLink || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={guardClick(manufacturerLink)}
                    aria-disabled={!manufacturerLink}
                    className={`flex-shrink-0 flex items-center justify-center h-7 w-[72px] rounded-lg border transition-colors ${
                        manufacturerLink ? LIVE_CHIP : MISSING_CHIP
                    }`}
                >
                    <img
                        src={brand.logo}
                        alt={brand.name}
                        className="max-h-4 max-w-[56px] w-auto object-contain"
                    />
                </a>
            </Tooltip>
            {!manufacturerOnly && (
                <Tooltip
                    content={
                        dmLink ? 'Open Dental Monitoring' : 'No Dental Monitoring link available'
                    }
                >
                    <a
                        href={dmLink || undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={guardClick(dmLink)}
                        aria-disabled={!dmLink}
                        className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg border transition-colors ${
                            dmLink ? LIVE_CHIP : MISSING_CHIP
                        }`}
                    >
                        <img
                            src={DM_LOGO.logo}
                            alt={DM_LOGO.name}
                            className="w-6 h-6 object-contain"
                        />
                    </a>
                </Tooltip>
            )}
        </div>
    );
}
